import { Ref, computed, ref, watch } from 'vue';

import type { FilterGroupOutput } from '@/components/filters/types';

import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import type { Feed } from '@/types/feeds';

interface UsePopulationAlertsProps {
  isCustomPopulation: Ref<boolean>;
  isEdit: Ref<boolean>;
  selectedFeed: Ref<Feed | null>;
  isFiltersEmpty: Ref<boolean>;
  hasRecommendedFiltersApplied: Ref<boolean>;
  capitalizedPopulation: Ref<string>;
  fallbackSuggestion: Ref<string>;
  advancedFilterGroups: Ref<FilterGroupOutput[]>;
}

export function usePopulationModalAlerts({
  isCustomPopulation,
  isEdit,
  selectedFeed,
  isFiltersEmpty,
  hasRecommendedFiltersApplied,
  capitalizedPopulation,
  fallbackSuggestion,
  advancedFilterGroups,
}: UsePopulationAlertsProps) {
  const hasClickedRefreshPreview = ref(false);

  const isSheetsOrCSV = computed(
    () =>
      selectedFeed.value?.integration?.type ===
        GOOGLE_SHEETS_DATA_SOURCE_TYPE ||
      selectedFeed.value?.integration?.type === FILE_UPLOAD_DATA_SOURCE_TYPE,
  );

  const dynamicDescription = computed(() => {
    if (isSheetsOrCSV.value) {
      return `Apply filters to include only accounts that are actual ${capitalizedPopulation.value.toLowerCase()}. This ensures your overlaps and insights are meaningful. Then click Preview to confirm the right accounts are included.`;
    }
    return `Apply filters to include only accounts that are actual <span class="font-bold">${capitalizedPopulation.value}</span>. This ensures your overlaps and insights are meaningful. Use filters
      like <span class="font-bold">${fallbackSuggestion.value}</span>,
      then click Preview to confirm the right accounts are included.`;
  });

  const callout = computed(() => ({
    ['recommended']: {
      message: 'Recommended filters for your CRM',
      description: `We’ve pre-filled filters to define your <span class="font-bold">${capitalizedPopulation.value}</span>, based on CRM and ecosystem best practices for accurate overlaps. Editable anytime.`,
    },
    ['define-standard']: {
      message: `Define your ${capitalizedPopulation.value}`,
      description: dynamicDescription.value,
    },
    ['no-filters-source-syncing']: {
      message: '',
      description:
        'Your CRM data is still syncing, so the preview may not include all available records yet.',
    },
    ['no-filters']: {
      message: 'No filters applied',
      description:
        'This population currently includes all synced accounts. To make it useful, add filters that reflect the criteria for this segment.',
    },
  }));

  const populationFiltersAlertVisibility = computed(() => {
    return !isCustomPopulation.value && !isEdit.value;
  });

  const populationFiltersAlertContent = computed(() => {
    if (!populationFiltersAlertVisibility.value) return null;
    if (hasRecommendedFiltersApplied.value) {
      return callout.value.recommended;
    }
    return callout.value['define-standard'];
  });

  const populationPreviewAlertVisibility = computed(() => {
    if (isEdit.value) return false;

    if (selectedFeed.value?.is_syncing) return true;
    return isFiltersEmpty.value && hasClickedRefreshPreview.value;
  });

  const populationPreviewAlertContent = computed(() => {
    if (!populationPreviewAlertVisibility.value) return null;

    if (selectedFeed.value?.is_syncing) {
      return callout.value['no-filters-source-syncing'];
    }

    return callout.value['no-filters'];
  });

  function handleUserRefreshPreview() {
    hasClickedRefreshPreview.value = true;
  }

  function resetRefreshPreviewFlag() {
    hasClickedRefreshPreview.value = false;
  }

  watch(
    advancedFilterGroups,
    () => {
      hasClickedRefreshPreview.value = false;
    },
    { deep: true },
  );

  return {
    hasClickedRefreshPreview,
    populationFiltersAlertVisibility,
    populationFiltersAlertContent,
    populationPreviewAlertVisibility,
    populationPreviewAlertContent,
    handleUserRefreshPreview,
    resetRefreshPreviewFlag,
  };
}
